/**
 * Preferences: the signed-in user's own email settings (Story 26, task 104).
 *
 * Every user has this page, Viewers included — it is the one write a
 * Viewer makes, and it touches only their own record. One overall toggle
 * (`enrolled`), and per Client in their scope a three-way choice: follow
 * the overall toggle, or override it on or off for that Client alone.
 * Each change saves as it is made. (The two ticket categories went with
 * MDR's ticketing module, task 193.)
 *
 * Two organization settings sit above all of it (task 208: shown on this
 * page, under the caller's own choices, with who set them, and editable by
 * an Admin of the caller's home organization), and the page says so when
 * either decides the outcome:
 *   - the role-level switch: while it is off for the caller's role, nothing
 *     is sent whatever they choose; their choices stay editable and apply
 *     when it comes back on;
 *   - the lock (Analysts and Viewers only): the role-level setting is final
 *     and the page is read-only. Stored choices are ignored, not erased, so
 *     they are shown dimmed and return when the lock is lifted.
 *
 * The server decides: `PATCH` answers 409 `preferences_locked` if the lock
 * landed after this page loaded, and the page refetches into its read-only
 * state.
 */

import type { ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  ApiError,
  listTenants,
  getMyEmailPreferences,
  updateMyEmailPreferences,
  type ClientTenant,
  type EmailState,
  type MyEmailPreferences,
  type UpdateMyEmailPreferencesBody,
} from '@bluesphere/api-client';

import { useAccessToken } from '../auth/useAccessToken';
import { useOrgAdmin } from '../auth/useOrgAdmin';
import { useSelectedTenant } from '../auth/useSelectedTenant';
import { useTenant } from '../auth/useTenant';
import { EmailSettingsCard } from '../components/EmailSettingsCard';
import { Switch } from '../components/ResponseSettingsModal';
import { EmptyState, ErrorState, Loading } from '../components/states';
import { orgLabel, sourceLabel } from '../lib/orgs';
import { ROLE_LABELS } from '../lib/users';

const PREFERENCES_KEY = ['me', 'preferences'];

type ClientChoice = 'follow' | EmailState;

const CHOICES: { value: ClientChoice; label: string }[] = [
  { value: 'follow', label: 'Follow overall' },
  { value: 'on', label: 'On' },
  { value: 'off', label: 'Off' },
];

export function Preferences() {
  const idToken = useAccessToken();
  const { me } = useTenant();
  const { orgs } = useSelectedTenant();
  const orgAdmin = useOrgAdmin();
  const queryClient = useQueryClient();

  const prefsQuery = useQuery({
    queryKey: PREFERENCES_KEY,
    queryFn: () => getMyEmailPreferences(idToken),
    enabled: Boolean(idToken),
  });
  // The same list the Clients page polls, already cut to the caller's scope.
  const tenantsQuery = useQuery({
    queryKey: ['tenants'],
    queryFn: async () => (await listTenants(idToken)).tenants,
    enabled: Boolean(idToken),
  });

  const mutation = useMutation<MyEmailPreferences, Error, UpdateMyEmailPreferencesBody>({
    mutationFn: (body) => updateMyEmailPreferences(body, idToken),
    onSuccess: (prefs) => queryClient.setQueryData(PREFERENCES_KEY, prefs),
    onError: (error) => {
      // The lock landed after this page loaded: show it.
      if (error instanceof ApiError && error.code === 'preferences_locked') {
        void queryClient.invalidateQueries({ queryKey: PREFERENCES_KEY });
      }
    },
  });

  if (prefsQuery.isLoading) {
    return (
      <div className="bs-card">
        <Loading />
      </div>
    );
  }
  if (prefsQuery.error || !prefsQuery.data) {
    return (
      <ErrorState
        title="Could not load your preferences"
        error={prefsQuery.error}
        onRetry={() => void prefsQuery.refetch()}
      />
    );
  }

  const prefs = prefsQuery.data;
  const roleLabel = me ? ROLE_LABELS[me.role] : 'your role';
  const readOnly = prefs.locked || mutation.isPending;
  const tenants: ClientTenant[] = tenantsQuery.data ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Preferences</h1>
        <p className="mt-1 text-sm text-white/60">
          Which alert and action emails reach you. Emails only ever cover Clients in your scope.
        </p>
      </div>

      {mutation.error &&
        !(mutation.error instanceof ApiError && mutation.error.code === 'preferences_locked') && (
          <ErrorState title="Could not save your preferences" error={mutation.error} />
        )}

      <section className="bs-card p-6">
        <h2 className="text-base font-semibold text-bluesphere-textOnLight">Email</h2>

        {prefs.locked ? (
          <Notice tone="locked">
            An Admin has turned email <strong>{prefs.role_email}</strong> for {roleLabel}s and
            locked it, so{' '}
            {prefs.role_email === 'on'
              ? 'you receive alert and action emails for every Client in your scope'
              : 'you receive no alert or action emails'}
            . Your own choices below are kept and apply again if the lock is lifted.
          </Notice>
        ) : (
          prefs.role_email === 'off' && (
            <Notice tone="off">
              An Admin has turned email off for {roleLabel}s, so nothing is sent to you whatever you
              choose here. Your choices apply when it is turned back on.
            </Notice>
          )
        )}

        <div className={`mt-5 space-y-6 ${prefs.locked ? 'opacity-60' : ''}`}>
          <Switch
            checked={prefs.enrolled}
            disabled={readOnly}
            onChange={(enrolled) => mutation.mutate({ enrolled })}
            label="Email me about alerts and actions"
            hint="The default for every Client in your scope. Override it for one Client below."
          />

          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-bluesphere-textSecondary">
              Per Client
            </h3>
            {tenantsQuery.isLoading ? (
              <Loading height="h-16" />
            ) : tenantsQuery.error ? (
              <ErrorState
                title="Could not load your Clients"
                error={tenantsQuery.error}
                onRetry={() => void tenantsQuery.refetch()}
              />
            ) : tenants.length === 0 ? (
              <EmptyState title="No Clients in your scope" />
            ) : (
              <ul className="mt-2 divide-y divide-bluesphere-border">
                {tenants.map((tenant) => {
                  const override = prefs.per_client[tenant.client_tenant_id];
                  const choice: ClientChoice = override ?? 'follow';
                  const effective: EmailState = override ?? (prefs.enrolled ? 'on' : 'off');
                  return (
                    <li
                      key={tenant.client_tenant_id}
                      className="flex flex-wrap items-center justify-between gap-3 py-3"
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-bluesphere-textOnLight">
                          {tenant.display_name}
                        </div>
                        <div className="text-xs text-bluesphere-muted">
                          {effective === 'on' ? 'Emails you' : 'No email'}
                          {override ? ' · overridden' : ''}
                        </div>
                      </div>
                      <ChoiceGroup
                        label={`Email for ${tenant.display_name}`}
                        value={choice}
                        disabled={readOnly}
                        onChange={(next) =>
                          mutation.mutate({
                            per_client: {
                              [tenant.client_tenant_id]: next === 'follow' ? null : next,
                            },
                          })
                        }
                      />
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </section>
      {/* The organization's email settings (Story 48): what applies to the
          caller's home and who set it; editable with manage_org there. */}
      {orgs.homeId && (
        <EmailSettingsCard
          orgId={orgs.homeId}
          orgName={orgLabel(orgs.byId.get(orgs.homeId))}
          isRoot={!orgs.byId.get(orgs.homeId)?.parent_id}
          canEdit={orgAdmin.at(orgs.homeId)}
          nameOf={(id) => sourceLabel(orgs, id)}
        />
      )}
    </div>
  );
}

function Notice({ tone, children }: { tone: 'locked' | 'off'; children: ReactNode }) {
  const classes =
    tone === 'locked'
      ? 'border-bluesphere-borderMuted bg-bluesphere-surfaceMuted text-bluesphere-textOnLight'
      : 'border-amber-300 bg-amber-50 text-amber-900';
  return (
    <p role="status" className={`mt-3 rounded-lg border px-4 py-3 text-sm ${classes}`}>
      {children}
    </p>
  );
}

/** Three buttons, one pressed: follow the overall toggle, or override on or off. */
function ChoiceGroup({
  label,
  value,
  disabled,
  onChange,
}: {
  label: string;
  value: ClientChoice;
  disabled: boolean;
  onChange: (next: ClientChoice) => void;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className="inline-flex rounded-full border border-bluesphere-borderMuted p-0.5"
    >
      {CHOICES.map((c) => (
        <button
          key={c.value}
          type="button"
          aria-pressed={value === c.value}
          disabled={disabled}
          onClick={() => value !== c.value && onChange(c.value)}
          className={
            'whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold transition-colors disabled:cursor-not-allowed ' +
            (value === c.value
              ? 'bg-bluesphere-primary text-white'
              : 'text-bluesphere-textSecondary hover:bg-bluesphere-surfaceMuted')
          }
        >
          {c.label}
        </button>
      ))}
    </div>
  );
}
